'use client'

import { ArrowUpRight, Mail, Play } from 'lucide-react'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { PageCta, PageHero } from '@/components/platform/v2/PageEdge'
import VideoEmbed from '@/components/platform/v2/VideoEmbed'

let rutubeIcon = '/assets/images/rutube.jpeg'
let telegramIcon = '/assets/images/telegram.png'
let youtubeIcon = '/assets/images/youtube.jpeg'
let vkIcon = '/assets/images/vk.jpeg'
let dzenIcon = '/assets/images/dzen.jpeg'

const CHANNELS = [
    {
        href: 'https://rutube.ru/channel/31821757',
        title: 'RUTUBE',
        text: 'Видео о возможностях платформы, клиентском пути и автоматизации бизнеса.',
        icon: rutubeIcon,
        banned: false,
    },
    {
        href: 'https://vk.com/vcentre_it',
        title: 'ВКонтакте',
        text: 'Новости компании, продуктовые материалы и события VCENTRE.',
        icon: vkIcon,
        banned: false,
    },
    {
        href: 'https://t.me/vcentre_it',
        title: 'Telegram',
        text: 'Короткие обновления, наблюдения команды и новые публикации.',
        icon: telegramIcon,
        banned: true,
    },
    {
        href: 'https://dzen.ru/vcentre',
        title: 'Дзен',
        text: 'Статьи о цифровых продуктах, клиентах и развитии бизнеса.',
        icon: dzenIcon,
        banned: false,
    },
    {
        href: 'https://www.youtube.com/@VcentreIT',
        title: 'YouTube',
        text: 'Видеообзоры VCENTRE.ONE и практические разборы инструментов.',
        icon: youtubeIcon,
        banned: true,
    },
]

const BannedStar = () => (
    <span
        aria-label="Запрещённая в РФ соцсеть"
        title="Запрещённая в РФ соцсеть"
        className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--deep-black))] text-sm font-bold leading-none text-red-500"
    >
        ★
    </span>
)

const VIDEOS = [
    {
        id: '8ad106194dc1b832f4a4de2972008b97',
        title: 'VCENTRE.ONE: почему бизнесу уже недостаточно сайта, CRM и чата',
        text: 'Как собрать разрозненные цифровые каналы в единый клиентский контур.',
    },
    {
        id: '4e3ce4b06e97fddf0c72736ccbcd8bd1',
        title: 'Личный кабинет, который возвращает клиентов',
        text: 'Как личный кабинет помогает повышать доверие, качество сервиса и повторные продажи.',
    },
    {
        id: '7e1d5531b4f1397c15867ed5ca150631',
        title: 'Клиент написал — а вы потеряли продажу?',
        text: 'Как чат VCENTRE.ONE связывает поддержку, коммуникации и карточку клиента.',
    },
]

const NewsPageComponent = () => (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
        <PageHero
            eyebrow="Медиацентр VCENTRE"
            title="Следите за тем, что мы создаём"
            text={
                <p>
                    Делимся развитием VCENTRE.ONE, показываем возможности платформы и говорим о том, как технологии помогают бизнесу
                    выстраивать отношения с клиентами.
                </p>
            }
            actions={
                <>
                    <a href="#videos" className="btn-primary">
                        <Play className="h-4 w-4" /> Смотреть материалы
                    </a>
                    <a href="#social" className="btn-ghost">
                        Наши каналы
                    </a>
                </>
            }
            visual={
                <img
                    src={'/assets/images/platform/news-mascot-hero.png'}
                    alt="Маскот VCENTRE.IT с микрофоном и планшетом «Медиацентр VCENTRE»"
                    width={1122}
                    height={1402}
                    loading="eager"
                    className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                />
            }
        />

        <Section id="social" tone="quiet">
            <SectionHeading
                eyebrow="Социальные сети"
                title="VCENTRE в ваших каналах"
                text="Выберите удобную площадку: везде публикуем материалы о продукте, практике внедрения и развитии компании."
            />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {CHANNELS.map((channel) => (
                    <a
                        key={channel.title}
                        href={channel.href}
                        target="_blank"
                        rel="noreferrer"
                        className="surface surface-hover flex h-full flex-col items-center text-center"
                    >
                        <div className="relative">
                            {channel.banned && <BannedStar />}
                            <img
                                src={channel.icon}
                                alt=""
                                className="h-14 w-14 rounded-xl border border-border/70 object-cover"
                                loading="lazy"
                            />
                        </div>
                        <p className="mt-5 text-lg font-bold text-foreground">{channel.title}</p>
                        <p className="body-text mt-2 flex-1">{channel.text}</p>
                        <span className="btn-link mt-5">
                            Перейти <ArrowUpRight className="h-4 w-4" />
                        </span>
                    </a>
                ))}
            </div>
        </Section>

        <Section id="videos">
            <SectionHeading
                eyebrow="Видео"
                title="Смотрите VCENTRE.ONE в действии"
                text="Коротко и по делу разбираем возможности платформы и сценарии работы с клиентами."
            />
            <div className="mt-10 grid gap-6 lg:grid-cols-3">
                {VIDEOS.map((video) => (
                    <article key={video.id} className="surface flex h-full flex-col p-4 sm:p-5">
                        <VideoEmbed url={`https://rutube.ru/play/embed/${video.id}`} title={video.title} className="rounded-lg" />
                        <h3 className="mt-5 text-lg font-bold text-foreground">{video.title}</h3>
                        <p className="body-text mt-2 flex-1">{video.text}</p>
                        <a href={`https://rutube.ru/video/${video.id}/`} target="_blank" rel="noreferrer" className="btn-link mt-5">
                            Открыть на RUTUBE <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </article>
                ))}
            </div>
        </Section>

        <Section>
            <PageCta
                eyebrow="Для СМИ и партнёров"
                title="Связаться с PR-службой"
                text={<p>По вопросам публикаций, комментариев, интервью и совместных информационных проектов напишите нашей команде.</p>}
                actions={
                    <>
                        <a href="mailto:help@vcentre.pro" className="btn-primary">
                            <Mail className="h-4 w-4" /> Связаться с PR-службой
                        </a>
                        <a href="/platform" className="btn-ghost">
                            Узнать о VCENTRE.ONE
                        </a>
                    </>
                }
                visual={
                    <img
                        src={'/assets/images/platform/news-mascot-cta.png'}
                        alt="Маскот VCENTRE.IT с пресс-бейджем и смартфоном"
                        width={1254}
                        height={1254}
                        loading="lazy"
                        className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                    />
                }
            />
        </Section>
    </div>
)

export default NewsPageComponent
